import * as Sentry from '@sentry/vue';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { attachNormalized, normalizeError } from './normalizeError';
import { redactUrl } from './redact';
import { ErrorKind, ErrorSource, type NormalizedError } from './errorTypes';
import { pushTrail } from './trail';

/**
 * The HTTP seam.
 *
 * `normalizeError` is the universal seam — it sees every error source. The interceptor owns only what
 * must happen once per response and must happen *even when nobody catches*:
 *
 *  1. 401 session-expiry -> logout. This used to live inside `LoadingStateModel.setError`, so the
 *     store calls without a try/catch never triggered it: an expired session on app load produced an
 *     unhandled rejection and a spinner that never stopped. Here it covers all 24 services at once.
 *  2. Trace correlation, captured at throw time while the context still exists.
 *  3. The trail entry, so a report can show what led up to the failure.
 *
 * It **decorates, never replaces**: the original `AxiosError` is re-rejected, because ~20 call sites
 * do `error instanceof AxiosError`. `normalizeError` reads the attached classification back, so the
 * work happens once even though it is called twice.
 */

export interface HttpErrorHooks {
  onSessionExpired: () => void;
}

/** Requests that intentionally bypass `apiClient` are not covered — see the plan's bypass table. */
const START_KEY = '__appStartedAt';

type TimedConfig = InternalAxiosRequestConfig & { [START_KEY]?: number };

const durationOf = (config?: TimedConfig): number | undefined => {
  const startedAt = config?.[START_KEY];
  return typeof startedAt === 'number' ? Date.now() - startedAt : undefined;
};

const labelFor = (normalized: NormalizedError): string =>
  `${normalized.request?.method ?? 'GET'} ${normalized.request?.path ?? '?'}`;

export const installHttpErrorInterceptor = (client: AxiosInstance, hooks: HttpErrorHooks): void => {
  client.interceptors.request.use(config => {
    (config as TimedConfig)[START_KEY] = Date.now();
    return config;
  });

  client.interceptors.response.use(
    response => {
      try {
        pushTrail({
          type: 'request',
          label: `${response.config?.method?.toUpperCase() ?? 'GET'} ${redactUrl(response.config?.url)?.path ?? '?'}`,
          status: response.status,
          ms: durationOf(response.config as TimedConfig)
        });
      } catch {
        // Never let bookkeeping turn a successful response into a failure.
      }
      return response;
    },
    (error: unknown) => {
      try {
        const normalized = normalizeError(error, { source: ErrorSource.HTTP });

        if (normalized.request) {
          normalized.request.durationMs = durationOf(
            (error as { config?: TimedConfig }).config
          );
          normalized.request.sentryTrace = Sentry.getTraceData()['sentry-trace'];
          // NOTE: only present if the backend sets it. Confirm with ts-api before relying on it.
          const headers = (error as { response?: { headers?: Record<string, string> } }).response?.headers;
          normalized.request.requestId = headers?.['x-request-id'];
        }

        pushTrail({
          type: 'request',
          label: labelFor(normalized),
          status: normalized.httpStatus,
          ms: normalized.request?.durationMs
        });

        if (error != null && typeof error === 'object') {
          attachNormalized(error, normalized);
        }

        if (normalized.kind === ErrorKind.AUTH_EXPIRED) {
          hooks.onSessionExpired();
        }
      } catch {
        /* eslint-disable-next-line no-console */
        console.error('[errors] response interceptor failed');
      }

      // Unchanged, so `instanceof AxiosError` keeps working at every existing call site.
      return Promise.reject(error);
    }
  );
};
